import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Container, Typography, Link, Stack, Divider } from '@mui/material';
import { styled } from '@mui/material/styles';
import { SHUTDOWN_MODE } from '../shutdownConfig';

const GlassyFooter = styled('footer')(({ theme }) => ({
  background: 'rgba(11, 16, 32, 0.6)',
  backdropFilter: 'blur(12px)',
  borderTop: '1px solid rgba(255, 255, 255, 0.08)',
  padding: theme.spacing(4, 0),
  position: 'relative',
  zIndex: 1,
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'linear-gradient(180deg, rgba(59,161,255,0.06) 0%, rgba(255,255,255,0.03) 100%)',
    zIndex: -1
  }
}));

const FooterLink = styled(Link)(({ theme }) => ({
  color: 'rgba(255, 255, 255, 0.85)',
  textDecoration: 'none',
  transition: 'all 0.2s ease-in-out',
  padding: theme.spacing(0.5, 2),
  borderRadius: theme.shape.borderRadius,
  '&:hover': {
    color: '#9FD6FF',
    background: 'rgba(255, 255, 255, 0.06)'
  }
}));

const Footer = () => {
  const currentYear = new Date().getFullYear();

  if (SHUTDOWN_MODE) {
    return (
      <GlassyFooter>
        <Container maxWidth="lg">
          <Box sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', md: 'row' },
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 2
          }}>
            <Typography 
              variant="body2" 
              sx={{ color: 'rgba(255, 255, 255, 0.8)', fontWeight: 500 }}
            >
              &copy; {currentYear} Pensive Products LLC. All rights reserved.
            </Typography>
            
            <Stack 
              direction={{ xs: 'column', sm: 'row' }} 
              spacing={{ xs: 1, sm: 2 }}
              alignItems="center"
            >
              <FooterLink component={RouterLink} to="/privacy">
                Privacy Policy
              </FooterLink>
              <FooterLink component={RouterLink} to="/terms">
                Terms of Use
              </FooterLink>
              <FooterLink component={RouterLink} to="/legal">
                Legal
              </FooterLink>
            </Stack>
          </Box>
        </Container>
      </GlassyFooter>
    );
  }

  return (
    <GlassyFooter>
      <Container maxWidth="lg">
        {/* App Store Download Section */}
        <Box sx={{ 
          display: 'flex', 
          flexDirection: { xs: 'column', md: 'row' },
          justifyContent: 'center',
          alignItems: 'center',
          gap: 2,
          mb: 4
        }}>
          <Typography 
            variant="body1" 
            sx={{ 
              color: 'rgba(255, 255, 255, 0.9)',
              fontWeight: 600
            }}
          >
            Take My ScanTag with you everywhere
          </Typography>
          
          <a 
            href="https://apps.apple.com/us/app/id6742469893" 
            target="_blank" 
            rel="noopener noreferrer"
            style={{ 
              display: 'inline-block',
              textDecoration: 'none',
              background: 'black',
              padding: '6px 12px',
              borderRadius: '8px',
              boxShadow: '0 2px 12px rgba(0, 0, 0, 0.5)'
            }}
          >
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center',
              gap: 1
            }}>
              <Box 
                component="img" 
                src="/apple-logo.svg" 
                alt="Apple Logo"
                sx={{ 
                  width: 20, 
                  height: 20,
                  filter: 'brightness(0) invert(1)'
                }}
              />
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <Typography variant="caption" sx={{ color: 'white', lineHeight: 1, fontSize: '0.7rem' }}>
                  Download on the
                </Typography>
                <Typography variant="body2" sx={{ color: 'white', fontWeight: 600, lineHeight: 1.2 }}>
                  App Store
                </Typography>
              </Box>
            </Box>
          </a>
        </Box>
        
        <Divider sx={{ mb: 3, borderColor: 'rgba(255,255,255,0.08)' }} />
        
        <Box sx={{ 
          display: 'flex', 
          flexDirection: { xs: 'column', md: 'row' },
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 2
        }}>
          <Typography 
            variant="body2" 
            sx={{ 
              color: 'rgba(255, 255, 255, 0.8)',
              fontWeight: 500
            }}
          >
            &copy; {currentYear} Pensive Products LLC. All rights reserved.
          </Typography>
          
          <Stack 
            direction={{ xs: 'column', sm: 'row' }} 
            spacing={{ xs: 1, sm: 2 }}
            alignItems="center"
          >
            <FooterLink component={RouterLink} to="/privacy">
              Privacy Policy
            </FooterLink>
            <FooterLink component={RouterLink} to="/terms">
              Terms of Use
            </FooterLink>
            <FooterLink component={RouterLink} to="/legal">
              Legal
            </FooterLink>
            <FooterLink component={RouterLink} to="/contact">
              Contact Us
            </FooterLink>
          </Stack>
        </Box>
      </Container>
    </GlassyFooter>
  );
};

export default Footer; 